import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { Dialog } from '../common/Dialog';
import { formatDateTime } from '../../utils/dateUtils';
import { normalizeAlarmType, alarmTypeShortLabel, formatAlarmReading } from '../../utils/alarmDisplay';

function formatElapsed(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export interface AlarmAcknowledgeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  alarmType: string;
  sensorName: string;
  serialDisplay: string;
  alarmValues: string | number | null | undefined;
  activatedAt: string | null | undefined;
  /** When set, duration is static (alarm ended / reset). Otherwise live elapsed from activatedAt while open. */
  alarmResetAt?: string | null;
  eventId: string | null | undefined;
  noteDraft: string;
  onNoteChange: (value: string) => void;
  onAcknowledge: () => void;
  isSubmitting: boolean;
  error: string | null;
  /** When true, acknowledge is disabled until noteDraft has non-whitespace content. Default true. */
  requireNote?: boolean;
}

export function AlarmAcknowledgeDialog({
  isOpen,
  onClose,
  alarmType,
  sensorName,
  serialDisplay,
  alarmValues,
  activatedAt,
  alarmResetAt,
  eventId,
  noteDraft,
  onNoteChange,
  onAcknowledge,
  isSubmitting,
  error,
  requireNote = true,
}: AlarmAcknowledgeDialogProps) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!isOpen || alarmResetAt || !activatedAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isOpen, alarmResetAt, activatedAt]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const startedDisplay = activatedAt ? formatDateTime(activatedAt) : '—';

  let durationLabel: string | null = null;
  if (activatedAt) {
    if (alarmResetAt) {
      durationLabel = formatElapsed(
        new Date(alarmResetAt).getTime() - new Date(activatedAt).getTime()
      );
    } else {
      durationLabel = formatElapsed(now - new Date(activatedAt).getTime());
    }
  }

  const noteOk = !requireNote || noteDraft.trim().length > 0;

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} panelClassName="max-w-lg">
      <div className="pr-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Alarm details</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Review the alarm and enter a note, then acknowledge.
        </p>
        <div className="space-y-2 text-sm mb-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-700 dark:text-gray-300">Type</span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                (() => {
                  const k = normalizeAlarmType(alarmType);
                  if (k === 'high') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
                  if (k === 'low') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
                  if (k === 'battery') return 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300';
                  if (k === 'missing_data') return 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300';
                  return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
                })()
              }`}
            >
              {alarmTypeShortLabel(alarmType)}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Sensor</span>{' '}
            <span className="text-gray-900 dark:text-white">{sensorName}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Serial</span>{' '}
            <span className="text-gray-900 dark:text-white font-mono text-xs sm:text-sm">{serialDisplay}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Reading</span>{' '}
            <span className="text-gray-900 dark:text-white">
              {formatAlarmReading(alarmType, alarmValues)}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">Started</span>{' '}
            <span className="text-gray-900 dark:text-white">{startedDisplay}</span>
          </div>
          {durationLabel !== null && (
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700 dark:text-gray-300">Duration</span>
              <span
                className={`font-mono tabular-nums font-semibold text-base ${
                  (() => {
                    const k = normalizeAlarmType(alarmType);
                    if (k === 'low') return 'text-blue-600 dark:text-blue-400';
                    if (k === 'battery') return 'text-rose-600 dark:text-rose-400';
                    if (k === 'missing_data') return 'text-violet-600 dark:text-violet-400';
                    return 'text-red-600 dark:text-red-400';
                  })()
                }`}
              >
                <Clock className="inline h-4 w-4 mr-1 opacity-90 align-text-bottom" aria-hidden />
                {durationLabel}
              </span>
            </div>
          )}
          <div className="text-xs text-gray-500 dark:text-gray-400 break-all">
            <span className="font-medium text-gray-600 dark:text-gray-300">Event</span> {eventId ?? '—'}
          </div>
        </div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Alarm notes <span className="text-red-600 dark:text-red-400">*</span>
        </label>
        <textarea
          className="w-full min-h-[100px] p-3 border border-gray-300 dark:border-gray-600 rounded-lg mb-3 bg-app-inset dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm resize-y"
          value={noteDraft}
          onChange={e => onNoteChange(e.target.value)}
          placeholder="What caused this alarm or what action was taken…"
          disabled={isSubmitting}
          required={requireNote}
          aria-required={requireNote}
        />
        {error && <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>}
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-app-inset dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Close
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm font-semibold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
            onClick={onAcknowledge}
            disabled={isSubmitting || !noteOk}
          >
            {isSubmitting ? 'Acknowledging…' : 'Acknowledge alarm'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
